import { Page } from '@dixa/lib-shared-habla';
import { createContext, useEffect } from 'react';

import { usersApi } from 'apis/users';
import { useIsUserBannedOnChannel, useUserBans } from 'hooks/useBans';
import { pubSub } from 'utils/pubSub';
import { useAppPathParams } from 'utils/url/useAppPathParams';

import {
  contactLoadFailed,
  contactLoading,
  contactNotFound,
  noContactSelected,
} from '../../utils/contactDetails.utils';

import { UserAssignedConversations } from './components/userAssignedConversations/userAssignedConversations';
import { UserBanHistory } from './components/userBanHistory/userBanHistory';
import { UserConversations } from './components/userConversations/userConversations';
import { UserInfo } from './components/userInfo/userInfo';

// Looks like the Dynamo-Postgre sync lag is longer
// than in other places, but it's fine,
// because this is not the main table on the page
const fetchDelay = 2000;

export const UserBanOnChatContext = createContext<{
  isBanned?: boolean;
  onBanStatusToggle?: () => void;
  isLoadingBanStatus: boolean;
}>({
  isLoadingBanStatus: false,
});

export function UserDetails(): JSX.Element {
  const { id } = useAppPathParams('/contacts/user/:id');
  const [getUser, { user, isLoading, isError, isUninitialized }] =
    usersApi.useLazyReadUserQuery({
      selectFromResult: ({ data, ...rest }) => ({
        user: data?.data,
        ...rest,
      }),
    });

  const {
    isBanned: isBannedOnChat,
    refreshStatus: isBannedOnChatRefreshStatus,
    asyncStatus: isBannedOnChatAsyncStatus,
  } = useIsUserBannedOnChannel(user?.id, 'widget_chat');

  const {
    items: bansHistoryItems,
    asyncStatus: bansHistoryAsyncStatus,
    hasNextPage: bansHistoryHasNextPage,
    hasPreviousPage: bansHistoryHasPreviousPage,
    nextPage: bansHistoryNextPage,
    previousPage: bansHistoryPreviousPage,
    refreshItems: bansHistoryRefreshItems,
    changeParams: bansHistoryChangeParams,
  } = useUserBans({
    end_user_id: id,
    limit: 6,
  });

  useEffect(() => {
    bansHistoryChangeParams({ end_user_id: id });
  }, [id, bansHistoryChangeParams]);

  useEffect(() => {
    const fetchUser = () => {
      if (id) {
        getUser({ id });
      }
    };

    fetchUser();

    const unsubscribe = pubSub.subscribe('userModalUserUpdated', fetchUser);

    return () => unsubscribe();
  }, [id, getUser]);

  if (!id) {
    return noContactSelected;
  }

  if (isLoading || isUninitialized) {
    return contactLoading;
  }

  if (isError) {
    return contactLoadFailed;
  }

  if (!user) {
    return contactNotFound;
  }

  const handleBanStatusToggle = async () => {
    isBannedOnChatRefreshStatus();

    setTimeout(() => {
      bansHistoryRefreshItems();
    }, fetchDelay);
  };

  return (
    <Page>
      <Page.Body>
        <Page.Section>
          <UserBanOnChatContext.Provider
            value={{
              isBanned: isBannedOnChat,
              onBanStatusToggle: handleBanStatusToggle,
              isLoadingBanStatus: isBannedOnChatAsyncStatus === 'loading',
            }}
          >
            <UserInfo user={user} />
          </UserBanOnChatContext.Provider>
        </Page.Section>

        <Page.Section title="Conversations">
          <UserConversations user={user} />
        </Page.Section>

        {user.type === 'member' && (
          <Page.Section title="Assigned conversations">
            <UserAssignedConversations user={user} />
          </Page.Section>
        )}

        {user.type === 'contact' && (
          <Page.Section title="Ban history">
            <UserBanHistory
              items={bansHistoryItems}
              asyncStatus={bansHistoryAsyncStatus}
              hasNextPage={bansHistoryHasNextPage}
              hasPreviousPage={bansHistoryHasPreviousPage}
              nextPage={bansHistoryNextPage}
              previousPage={bansHistoryPreviousPage}
            />
          </Page.Section>
        )}
      </Page.Body>
    </Page>
  );
}
